import { ContactRequestSchema, ContactResponseSchema } from "./contact.js";
import { z } from "zod";
import { AmyVersionResponseSchema, HealthAiConsentRequestSchema, HealthAiConsentResponseSchema, HealthAiConsentWithdrawalResponseSchema, MePatchRequestSchema, MeResponseSchema, } from "./me.js";
import { HardConstraintsPutRequestSchema, HardConstraintsResponseSchema, ProfilePatchRequestSchema, ProfilePhotoUploadRequestSchema, ProfilePhotoUploadResponseSchema, ProfileResponseSchema, } from "./profile.js";
import { OnboardingStateResponseSchema, OnboardingStepPutRequestSchema, OnboardingStepPutResponseSchema, } from "./onboarding.js";
import { PlanJobCreateRequestSchema, PlanJobResponseSchema } from "./planJobs.js";
import { DayImpactPreviewRequestSchema, DayImpactPreviewResponseSchema, DayLedgerResponseSchema, OffPlanEntryCreateRequestSchema, OffPlanEntryCreateResponseSchema, } from "./ledger.js";
import { ProductStateResponseSchema } from "./productState.js";
import { PlanApplyRequestSchema, PlanScopeRequestSchema, PlanChangesQuerySchema, PlanChangeProofSchema, PlanScopeResponseSchema, RecipeExclusionListResponseSchema, RecipeExclusionPutRequestSchema, UsualFoodsResponseSchema, PlanMutationResultSchema, PlanProposalCandidatesRequestSchema, PlanTimeFitRequestSchema, PlanTimeFitResponseSchema, PlanProposalCandidatesResponseSchema, PlanProposalRequestSchema, PlanProposalResponseSchema, PlanResponseSchema, PlanUndoRequestSchema, } from "./plans.js";
import { PantryConfirmationRequestSchema, PantryExtractionRequestSchema, PantryExtractionResponseSchema, PantryItemUpdateRequestSchema, PantryResponseSchema, } from "./pantry.js";
import { PrepareBatchRequestSchema, PrepareBatchResponseSchema, PreparedBatchesResponseSchema } from "./preparedBatches.js";
import { ShoppingListPatchRequestSchema, ShoppingListResponseSchema, ShoppingPurchaseRequestSchema } from "./shopping.js";
import { OffersResponseSchema, OffersScopeSchema } from "./offers.js";
import { MealOutcomeRequestSchema, MealOutcomeResponseSchema } from "./meals.js";
import { ConsumptionHistoryQuerySchema, ConsumptionHistoryResponseSchema } from "./history.js";
import { LearnedPreferenceDeleteResponseSchema, LearnedPreferencesResponseSchema, MemoryCandidateActionResponseSchema, MemoryCandidateConfirmRequestSchema, MemoryCandidateCreateRequestSchema, MemoryCandidateRejectRequestSchema, MemoryCandidatesResponseSchema, DossierResponseSchema, AmyConversationResponseSchema, AmyHistoryResponseSchema, } from "./memory.js";
import { InsightsResponseSchema } from "./insights.js";
import { ProgressSummaryResponseSchema, ProgressWeightLogRequestSchema, ProgressWeightLogResponseSchema, ProgressWeightListResponseSchema, } from "./progress.js";
import { BillingCancelRequestSchema, BillingCancelResponseSchema, BillingSubscriptionResponseSchema, BillingCheckoutRequestSchema, BillingCheckoutResponseSchema, BillingOffersQuerySchema, BillingOffersResponseSchema, BillingAppleTransactionRequestSchema, BillingAppleTransactionResponseSchema, BillingFreeAccessRequestSchema, BillingFreeAccessResponseSchema, } from "./billing.js";
import { AppleServerNotificationSchema, AppleWebhookAckResponseSchema, WhopWebhookAckResponseSchema, WhopWebhookEventSchema, } from "./webhooks.js";
import { DeletionJobRequestSchema, DeletionJobResponseSchema, ExportJobRequestSchema, ExportJobResponseSchema, } from "./jobs.js";
import { EmailUnsubscribeQuerySchema, EmailUnsubscribeResponseSchema } from "./emailUnsubscribe.js";
import { IngredientResolveRequestSchema, IngredientResolveResponseSchema } from "./ingredients.js";
import { RecipeDetailResponseSchema, RecipeCatalogSearchQuerySchema, RecipeCatalogSearchResponseSchema } from "./recipes.js";
import { ComposeRecipeRequestSchema, ComposeRecipeResponseSchema } from "./composeRecipe.js";
import { LlmUsageRecordRequestSchema, LlmUsageRecordResponseSchema, LlmUsageResponseSchema } from "./usage.js";
import { AmyTurnCompleteRequestSchema, AmyTurnRegisterRequestSchema, AmyTurnRegisterResponseSchema, AmyTurnResponseSchema, } from "./amyTurns.js";
const idParam = z.object({ id: z.string() });
export const endpoints = [
    {
        operationId: "getMe",
        method: "get",
        path: "/v1/me",
        summary: "Account, locale, onboarding state, entitlement summary",
        tags: ["account"],
        response: MeResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "patchMe",
        method: "patch",
        path: "/v1/me",
        summary: "Set the caller's locale — the language the API answers catalogue names in",
        tags: ["account"],
        body: MePatchRequestSchema,
        response: MeResponseSchema,
        // Setting a locale is naturally idempotent, but the envelope rule has no
        // exception for "naturally idempotent" and should not grow one: the client
        // sends a key like every other mutation.
        requiresIdempotencyKey: true,
    },
    {
        operationId: "postHealthAiConsent",
        method: "post",
        path: "/v1/me/health-ai-consent",
        summary: "Record explicit consent to process health data with the third-party AI (GDPR Art. 9(2)(a)); 400 UNKNOWN_CONSENT_VERSION for a stale wording version",
        tags: ["account"],
        body: HealthAiConsentRequestSchema,
        response: HealthAiConsentResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "deleteHealthAiConsent",
        method: "delete",
        path: "/v1/me/health-ai-consent",
        summary: "Withdraw the health/AI consent (GDPR Art. 7(3)): closes every consent gate and erases the health data it covered in one transaction; never gated itself",
        tags: ["account"],
        response: HealthAiConsentWithdrawalResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getAmyVersion",
        method: "get",
        path: "/v1/amy/version",
        summary: "Currently deployed Amy behavior version (#100 client handshake)",
        tags: ["account"],
        response: AmyVersionResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getProfile",
        method: "get",
        path: "/v1/profile",
        summary: "Explicit profile and goal inputs",
        tags: ["profile"],
        response: ProfileResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "patchProfile",
        method: "patch",
        path: "/v1/profile",
        summary: "Update explicit profile and goal inputs",
        tags: ["profile"],
        body: ProfilePatchRequestSchema,
        response: ProfileResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "createProfilePhotoUpload",
        method: "post",
        path: "/v1/profile/photo-upload",
        summary: "Short-lived presigned PUT URL for the caller's avatar (ADR-024; client confirms via PATCH personalInfo.photoKey)",
        tags: ["profile"],
        body: ProfilePhotoUploadRequestSchema,
        response: ProfilePhotoUploadResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getHardConstraints",
        method: "get",
        path: "/v1/profile/hard-constraints",
        summary: "Current active hard-constraint set with its wire revision",
        tags: ["profile"],
        response: HardConstraintsResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "putHardConstraints",
        method: "put",
        path: "/v1/profile/hard-constraints",
        summary: "Replace validated explicit hard constraints after confirmation",
        tags: ["profile"],
        body: HardConstraintsPutRequestSchema,
        response: HardConstraintsResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getOnboarding",
        method: "get",
        path: "/v1/onboarding",
        summary: "Step state and current answers",
        tags: ["onboarding"],
        response: OnboardingStateResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "putOnboardingStep",
        method: "put",
        path: "/v1/onboarding/{step}",
        summary: "Save one mandatory explicit answer; 'none' is a real value",
        tags: ["onboarding"],
        params: z.object({ step: z.string() }),
        body: OnboardingStepPutRequestSchema,
        response: OnboardingStepPutResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "createPlanJob",
        method: "post",
        path: "/v1/plan-jobs",
        summary: "Start idempotent weekly plan generation",
        tags: ["plans"],
        body: PlanJobCreateRequestSchema,
        response: PlanJobResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getPlanJob",
        method: "get",
        path: "/v1/plan-jobs/{id}",
        summary: "Plan job status (queued|running|validating|completed|failed)",
        tags: ["plans"],
        params: idParam,
        response: PlanJobResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getProductState",
        method: "get",
        path: "/v1/product-state",
        summary: "Amy truth contract: capabilities, setting activity, latest plan-job semantics",
        tags: ["plans"],
        response: ProductStateResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getDayLedger",
        method: "get",
        path: "/v1/ledger/today",
        summary: "Whole-day ledger: consumed, planned remaining, projected vs target",
        tags: ["ledger"],
        response: DayLedgerResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getConsumptionHistory",
        method: "get",
        path: "/v1/history/meals",
        summary: "Consumption history (ADR-056): the user's canonical recorded meals per day between from and to (inclusive, ≤ 62 days) — immutable snapshots independent of plan revisions; Plan renders past-day outcome marks from this, never from wire meal ids",
        tags: ["ledger"],
        query: ConsumptionHistoryQuerySchema,
        response: ConsumptionHistoryResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "previewDayImpact",
        method: "post",
        path: "/v1/ledger/preview",
        summary: "Pre-eating 'can I eat X?' projection — strictly non-mutating",
        tags: ["ledger"],
        body: DayImpactPreviewRequestSchema,
        response: DayImpactPreviewResponseSchema,
        requiresIdempotencyKey: false,
        isReadOnlyPost: true,
    },
    {
        operationId: "createOffPlanEntry",
        method: "post",
        path: "/v1/ledger/entries",
        summary: "Log off-plan food with source/confidence; returns the updated ledger",
        tags: ["ledger"],
        body: OffPlanEntryCreateRequestSchema,
        response: OffPlanEntryCreateResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "deleteOffPlanEntry",
        method: "delete",
        path: "/v1/ledger/entries/{id}",
        summary: "Remove a mislogged off-plan entry; returns the updated ledger",
        tags: ["ledger"],
        params: idParam,
        response: DayLedgerResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "discoverPlanScope",
        method: "post",
        path: "/v1/plans/current/scope",
        summary: "Server-side categorical scope discovery on the active plan — strictly non-mutating",
        tags: ["plans"],
        body: PlanScopeRequestSchema,
        response: PlanScopeResponseSchema,
        requiresIdempotencyKey: false,
        isReadOnlyPost: true,
    },
    {
        operationId: "getPlanChanges",
        method: "get",
        path: "/v1/plans/current/changes",
        summary: "ADR-075 change proof: what the active plan version (or a given job's result) changed vs its parent — changed meals by (date, slot), ingredient use before/after, shopping delta; read-only, computed from retained revisions, no revision numbers on the wire",
        tags: ["plans"],
        query: PlanChangesQuerySchema,
        response: PlanChangeProofSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "putRecipeExclusion",
        method: "put",
        path: "/v1/preferences/recipe-exclusions",
        summary: "Persist or reverse a durable 'never plan this recipe again' exclusion",
        tags: ["profile"],
        body: RecipeExclusionPutRequestSchema,
        response: RecipeExclusionListResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "listRecipeExclusions",
        method: "get",
        path: "/v1/preferences/recipe-exclusions",
        summary: "List active durable recipe exclusions",
        tags: ["profile"],
        response: RecipeExclusionListResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getUsualFoods",
        method: "get",
        path: "/v1/plans/usual-foods",
        summary: "Observed usual-foods digest (deterministic frequency+recency over confirmed behavior)",
        tags: ["plans"],
        response: UsualFoodsResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getCurrentPlan",
        method: "get",
        path: "/v1/plans/current",
        summary: "Accepted plan and revision",
        tags: ["plans"],
        response: PlanResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "createPlanProposal",
        method: "post",
        path: "/v1/plans/{id}/proposals",
        summary: "Deterministic scoped swap/adaptation request (no persistence)",
        tags: ["plans"],
        params: idParam,
        body: PlanProposalRequestSchema,
        response: PlanProposalResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "listPlanProposalCandidates",
        method: "post",
        path: "/v1/plans/{id}/proposals/candidates",
        summary: "The 3-5 replacement dishes a swap would choose from, in the swap's score order (read-only: no proposal, no token)",
        tags: ["plans"],
        params: idParam,
        body: PlanProposalCandidatesRequestSchema,
        response: PlanProposalCandidatesResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "fitPlanMealToTime",
        method: "post",
        path: "/v1/plans/{id}/time-fit",
        summary: "Deterministic time fit: the server picks the meal, the dish and the portion for the user's stated time and returns a ready proposal text (DE + EN)",
        tags: ["plans"],
        params: idParam,
        body: PlanTimeFitRequestSchema,
        response: PlanTimeFitResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "applyPlanProposal",
        method: "post",
        path: "/v1/plans/{id}/proposals/{proposalId}/apply",
        summary: "Apply a validated proposal atomically using its confirmation token",
        tags: ["plans"],
        params: z.object({ id: z.string(), proposalId: z.string() }),
        body: PlanApplyRequestSchema,
        response: PlanMutationResultSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "undoPlan",
        method: "post",
        path: "/v1/plans/{id}/undo",
        summary: "Undo the last apply as a new revision equal to the parent's payload",
        tags: ["plans"],
        params: idParam,
        body: PlanUndoRequestSchema,
        response: PlanMutationResultSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getRecipe",
        method: "get",
        path: "/v1/recipes/{id}",
        summary: "Recipe detail: steps, ingredients, aggregated allergens, computed nutrition",
        tags: ["recipes"],
        params: idParam,
        response: RecipeDetailResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "searchRecipeCatalog",
        method: "get",
        path: "/v1/recipes",
        summary: "Catalog-wide recipe search by title substring — resolves an existing recipe's id even when it is not on the user's current plan",
        tags: ["recipes"],
        query: RecipeCatalogSearchQuerySchema,
        response: RecipeCatalogSearchResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getPantry",
        method: "get",
        path: "/v1/pantry",
        summary: "Confirmed inventory and uncertainty",
        tags: ["pantry"],
        response: PantryResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "extractPantryItems",
        method: "post",
        path: "/v1/pantry/extractions",
        summary: "Non-mutating text candidate extraction",
        tags: ["pantry"],
        body: PantryExtractionRequestSchema,
        response: PantryExtractionResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "confirmPantryItems",
        method: "post",
        path: "/v1/pantry/confirmations",
        summary: "Persist user-edited candidates using the confirmation token",
        tags: ["pantry"],
        body: PantryConfirmationRequestSchema,
        response: PantryResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "updatePantryItem",
        method: "patch",
        path: "/v1/pantry/items/{id}",
        summary: "Directly edit one confirmed pantry item's quantity/expiry (user-authored, no extraction involved)",
        tags: ["pantry"],
        params: idParam,
        body: PantryItemUpdateRequestSchema,
        response: PantryResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "removePantryItem",
        method: "delete",
        path: "/v1/pantry/items/{id}",
        summary: "Remove one pantry item from inventory (soft: state becomes discarded, event-logged)",
        tags: ["pantry"],
        params: idParam,
        response: PantryResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getCurrentShoppingList",
        method: "get",
        path: "/v1/shopping-lists/current",
        summary: "Calculated + manual shopping list with revision control",
        tags: ["shopping"],
        response: ShoppingListResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "patchCurrentShoppingList",
        method: "patch",
        path: "/v1/shopping-lists/current",
        summary: "Add/remove manual items or toggle check-off state",
        tags: ["shopping"],
        body: ShoppingListPatchRequestSchema,
        response: ShoppingListResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getPreparedBatches",
        method: "get",
        path: "/v1/prepared-batches",
        summary: "Prepared Food Inventory (ADR-065): batches the user confirmed they cooked, with the portions remaining, plus the batches the current plan schedules that are not prepared yet. Never inferred from the plan.",
        tags: ["pantry"],
        response: PreparedBatchesResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "prepareBatch",
        method: "post",
        path: "/v1/prepared-batches",
        summary: "Mark a scheduled batch as prepared (ADR-065): deducts the raw ingredients for the portions actually cooked from the authoritative Pantry (never below zero; uncovered ingredients are reported, never invented) and creates the prepared-portion inventory. Atomic and idempotent.",
        tags: ["pantry"],
        body: PrepareBatchRequestSchema,
        response: PrepareBatchResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "confirmShoppingPurchases",
        method: "post",
        path: "/v1/shopping-lists/current/purchases",
        summary: "WS3 Shop → Pantry v1: confirm what came home from the shop per row (bought / already had / not bought) or mark the shop done; confirmed quantities enter the pantry as idempotent inventory events",
        tags: ["shopping"],
        body: ShoppingPurchaseRequestSchema,
        response: ShoppingListResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getOffers",
        method: "get",
        path: "/v1/offers",
        summary: "Currently valid retailer offers deterministically matched to the caller's plan or shopping list (ADR-028; empty array when the flag is off or nothing matches)",
        tags: ["offers"],
        query: z.object({ scope: OffersScopeSchema }),
        response: OffersResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "recordMealOutcome",
        method: "post",
        path: "/v1/meals/{id}/outcomes",
        summary: "Completion and structured/free-text feedback",
        tags: ["meals"],
        params: idParam,
        body: MealOutcomeRequestSchema,
        response: MealOutcomeResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "createMemoryCandidate",
        method: "post",
        path: "/v1/memory-candidates",
        summary: "Propose a learnable preference candidate (Amy's propose_memory tool; pending until confirm/reject)",
        tags: ["memory"],
        body: MemoryCandidateCreateRequestSchema,
        response: MemoryCandidateActionResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getMemoryCandidates",
        method: "get",
        path: "/v1/memory-candidates",
        summary: "Pending/confirmed learned items with provenance/effect",
        tags: ["memory"],
        response: MemoryCandidatesResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "confirmMemoryCandidate",
        method: "post",
        path: "/v1/memory-candidates/{id}/confirm",
        summary: "Explicit durable-memory decision (confirm)",
        tags: ["memory"],
        params: idParam,
        body: MemoryCandidateConfirmRequestSchema,
        response: MemoryCandidateActionResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "rejectMemoryCandidate",
        method: "post",
        path: "/v1/memory-candidates/{id}/reject",
        summary: "Explicit durable-memory decision (reject)",
        tags: ["memory"],
        params: idParam,
        body: MemoryCandidateRejectRequestSchema,
        response: MemoryCandidateActionResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getLearnedPreferences",
        method: "get",
        path: "/v1/learned-preferences",
        summary: "Confirmed durable preferences (Memory Center list): wording, origin, confidence",
        tags: ["memory"],
        response: LearnedPreferencesResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "deleteLearnedPreference",
        method: "delete",
        path: "/v1/learned-preferences/{id}",
        summary: "Delete/unlearn a soft preference",
        tags: ["memory"],
        params: idParam,
        response: LearnedPreferenceDeleteResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getDossier",
        method: "get",
        path: "/v1/context/dossier",
        summary: "Per-user dossier for Amy: confirmed notes + deterministic progress/activity digests (ADR-017)",
        tags: ["memory"],
        response: DossierResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getAmyHistory",
        method: "get",
        path: "/v1/amy/history",
        summary: "Restore Amy's recent chat transcript (last 30 days, up to 50 messages) from AgentCore Memory",
        tags: ["memory"],
        response: AmyHistoryResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getAmyConversation",
        method: "get",
        path: "/v1/amy/conversation",
        summary: "Durable Amy conversation identity for this user (creates or adopts on first call) — #106/ADR-045",
        tags: ["memory"],
        response: AmyConversationResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "startNewAmyConversation",
        method: "post",
        path: "/v1/amy/conversation",
        summary: "Archive the active Amy conversation and mint a fresh durable conversationId (new chat)",
        tags: ["memory"],
        response: AmyConversationResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "registerAmyTurn",
        method: "post",
        path: "/v1/amy/turns",
        summary: "ADR-079: the runtime registers the Amy turn it is about to run (idempotent on turnId; created:false = duplicate)",
        tags: ["amy"],
        body: AmyTurnRegisterRequestSchema,
        response: AmyTurnRegisterResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getAmyTurn",
        method: "get",
        path: "/v1/amy/turns/{id}",
        summary: "ADR-079: the durable status of one Amy turn — what a client recovers from after a lost stream (status only, never text)",
        tags: ["amy"],
        params: idParam,
        response: AmyTurnResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "completeAmyTurn",
        method: "patch",
        path: "/v1/amy/turns/{id}",
        summary: "ADR-079: the runtime records the terminal status of a turn (completed / failed / abandoned); guarded to a running row",
        tags: ["amy"],
        params: idParam,
        body: AmyTurnCompleteRequestSchema,
        response: AmyTurnResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getLlmUsage",
        method: "get",
        path: "/v1/usage/llm",
        summary: "Calling user's month-to-date LLM token usage and budget state (ADR-016)",
        tags: ["usage"],
        response: LlmUsageResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "recordLlmUsage",
        method: "post",
        path: "/v1/usage/llm",
        summary: "Record one metered LLM call's token counts; cost is priced server-side",
        tags: ["usage"],
        body: LlmUsageRecordRequestSchema,
        response: LlmUsageRecordResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getInsightsValue",
        method: "get",
        path: "/v1/insights/value",
        summary: "Sourced realized/projected savings, time, waste, adherence",
        tags: ["insights"],
        response: InsightsResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "logProgressWeight",
        method: "post",
        path: "/v1/progress/weight",
        summary: "Log (or correct) one day's weigh-in — ADR-027 progress model",
        tags: ["progress"],
        body: ProgressWeightLogRequestSchema,
        response: ProgressWeightLogResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getProgressWeightHistory",
        method: "get",
        path: "/v1/progress/weight",
        summary: "Last 90 days of logged weigh-ins",
        tags: ["progress"],
        response: ProgressWeightListResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getProgressSummary",
        method: "get",
        path: "/v1/progress/summary",
        summary: "Consistency, 7-day weight trend, and calibration state — observable facts only, never a verdict",
        tags: ["progress"],
        response: ProgressSummaryResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "getBillingOffers",
        method: "get",
        path: "/v1/billing/offers",
        summary: "Localized price/trial/renewal terms from server configuration",
        tags: ["billing"],
        query: BillingOffersQuerySchema,
        response: BillingOffersResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "createBillingCheckout",
        method: "post",
        path: "/v1/billing/checkout",
        summary: "Create hosted checkout after explicit user plan selection",
        tags: ["billing"],
        body: BillingCheckoutRequestSchema,
        response: BillingCheckoutResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "getBillingSubscription",
        method: "get",
        path: "/v1/billing/subscription",
        summary: "Caller's current subscription with provider-fresh trial/renewal/cancellation detail",
        tags: ["billing"],
        response: BillingSubscriptionResponseSchema,
        requiresIdempotencyKey: false,
    },
    {
        operationId: "cancelBillingSubscription",
        method: "post",
        path: "/v1/billing/cancel",
        summary: "Cancel the caller's active membership at period end via the provider admin API (ADR-032c)",
        tags: ["billing"],
        body: BillingCancelRequestSchema,
        response: BillingCancelResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "whopWebhook",
        method: "post",
        path: "/v1/webhooks/whop",
        summary: "Signed provider webhook; no end-user JWT",
        tags: ["billing"],
        body: WhopWebhookEventSchema,
        response: WhopWebhookAckResponseSchema,
        requiresIdempotencyKey: false,
        isWebhook: true,
    },
    {
        operationId: "submitAppleTransaction",
        method: "post",
        path: "/v1/billing/apple/transactions",
        summary: "Submit a signed StoreKit transaction after a purchase or restore; verified server-side and applied idempotently (ADR-085)",
        tags: ["billing"],
        body: BillingAppleTransactionRequestSchema,
        response: BillingAppleTransactionResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "grantFreeAccess",
        method: "post",
        path: "/v1/billing/free-access",
        summary: "Grant the 14-day free period once per account (server-side, provider bitebalance; ADR-085)",
        tags: ["billing"],
        body: BillingFreeAccessRequestSchema,
        response: BillingFreeAccessResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "appleServerNotification",
        method: "post",
        path: "/v1/webhooks/apple",
        summary: "App Store Server Notifications V2 (signed JWS); no end-user JWT",
        tags: ["billing"],
        body: AppleServerNotificationSchema,
        response: AppleWebhookAckResponseSchema,
        requiresIdempotencyKey: false,
        isWebhook: true,
    },
    {
        operationId: "getEmailUnsubscribe",
        method: "get",
        path: "/v1/email-unsubscribe",
        summary: "One-click offer-email unsubscribe (ADR-029/RFC 8058): tokenized, auth-free; flips offerEmailsOptIn to false and returns a localized HTML confirmation page; invalid token is a plain 404",
        tags: ["account"],
        query: EmailUnsubscribeQuerySchema,
        response: EmailUnsubscribeResponseSchema,
        requiresIdempotencyKey: false,
        isPublicTokenRoute: true,
        responseContentType: "text/html",
    },
    {
        operationId: "postEmailUnsubscribe",
        method: "post",
        path: "/v1/email-unsubscribe",
        summary: "RFC 8058 List-Unsubscribe-Post target (mail providers POST List-Unsubscribe=One-Click); same tokenized auth-free semantics as the GET",
        tags: ["account"],
        query: EmailUnsubscribeQuerySchema,
        response: EmailUnsubscribeResponseSchema,
        requiresIdempotencyKey: false,
        isPublicTokenRoute: true,
        responseContentType: "text/html",
    },
    {
        operationId: "postContact",
        method: "post",
        path: "/v1/contact",
        summary: "Public contact form (the Impressum's second contact channel): no account needed; forwards the message by email to hello@ and returns a receipt id shown on the page",
        tags: ["account"],
        body: ContactRequestSchema,
        response: ContactResponseSchema,
        requiresIdempotencyKey: false,
        isPublicForm: true,
    },
    {
        operationId: "resolveIngredient",
        method: "post",
        path: "/v1/ingredients/resolve",
        summary: "Resolve a free-text ingredient name to a canonical registry ID, or enqueue sourcing",
        tags: ["ingredients"],
        body: IngredientResolveRequestSchema,
        response: IngredientResolveResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "composeRecipe",
        method: "post",
        path: "/v1/recipes/compose",
        summary: "Ground, validate, and (if valid) persist an Amy-composed recipe structure (ADR-022 R1)",
        tags: ["recipes"],
        body: ComposeRecipeRequestSchema,
        response: ComposeRecipeResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "createExportJob",
        method: "post",
        path: "/v1/export-jobs",
        summary: "GDPR export workflow",
        tags: ["privacy"],
        body: ExportJobRequestSchema,
        response: ExportJobResponseSchema,
        requiresIdempotencyKey: true,
    },
    {
        operationId: "createDeletionJob",
        method: "post",
        path: "/v1/deletion-jobs",
        summary: "Confirmed account deletion workflow",
        tags: ["privacy"],
        body: DeletionJobRequestSchema,
        response: DeletionJobResponseSchema,
        requiresIdempotencyKey: true,
    },
];
export const getEndpoint = (operationId) => {
    const found = endpoints.find((e) => e.operationId === operationId);
    if (!found)
        throw new Error(`Unknown endpoint operationId: ${operationId}`);
    return found;
};
//# sourceMappingURL=endpoints.js.map